import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  ReactFlowProvider,
  useZoomPanHelper,
  useStoreState,
  isNode,
} from 'react-flow-renderer';
import dagre from 'dagre';

// Color palette for classification groups
const CLASSIFICATION_COLORS = [
  { bg: '#e8daef', border: '#8e44ad', text: '#6c3483' },
  { bg: '#d5f5e3', border: '#27ae60', text: '#1e8449' },
  { bg: '#fadbd8', border: '#e74c3c', text: '#c0392b' },
  { bg: '#d6eaf8', border: '#2980b9', text: '#1f618d' },
  { bg: '#fdebd0', border: '#e67e22', text: '#ca6f1e' },
  { bg: '#d5dbdb', border: '#566573', text: '#2c3e50' },
  { bg: '#fcf3cf', border: '#f1c40f', text: '#b7950b' },
  { bg: '#d1f2eb', border: '#1abc9c', text: '#148f77' },
  { bg: '#f5cba7', border: '#d35400', text: '#a04000' },
  { bg: '#aed6f1', border: '#2471a3', text: '#1a5276' },
];

const TYPE_COLORS = [
  { bg: '#ebf5fb', border: '#3498db' },
  { bg: '#eafaf1', border: '#2ecc71' },
  { bg: '#fef9e7', border: '#f39c12' },
  { bg: '#fdedec', border: '#e74c3c' },
  { bg: '#f4ecf7', border: '#9b59b6' },
  { bg: '#e8f8f5', border: '#1abc9c' },
  { bg: '#fbeee6', border: '#e67e22' },
  { bg: '#eaecee', border: '#7f8c8d' },
];

const NODE_WIDTH = 180;
const NODE_HEIGHT = 50;

function getLayoutedElements(elements, direction = 'TB') {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, ranksep: 80, nodesep: 40 });

  const nodes = elements.filter(isNode);
  const edges = elements.filter(el => !isNode(el));

  nodes.forEach((node) => {
    g.setNode(node.id, {
      width: (node.style && node.style.width) || NODE_WIDTH,
      height: NODE_HEIGHT,
    });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  return elements.map((el) => {
    if (isNode(el)) {
      const n = g.node(el.id);
      const w = (el.style && el.style.width) || NODE_WIDTH;
      return {
        ...el,
        position: {
          x: n.x - w / 2,
          y: n.y - NODE_HEIGHT / 2,
        },
      };
    }
    return el;
  });
}

function sumTree(obj) {
  let m = 0, a = 0;
  for (const v of Object.values(obj)) {
    if (v && typeof v.models === 'number') { m += v.models; a += v.assets; }
    else if (v && typeof v === 'object') { const r = sumTree(v); m += r.m; a += r.a; }
  }
  return { m, a };
}

// Collect all model entries from any level of the tree
function collectModelsFromTree(obj) {
  const result = [];
  for (const v of Object.values(obj)) {
    if (v && typeof v.models === 'number' && v.modelEntries) {
      result.push(...v.modelEntries);
    } else if (v && typeof v === 'object') {
      result.push(...collectModelsFromTree(v));
    }
  }
  return result;
}

// Download CSV of assets under a set of model entries
function downloadAssetsCSV(modelEntries, assetsByModelId, filename) {
  const rows = [];
  for (const entry of modelEntries) {
    const assets = assetsByModelId[entry.model_id] || [];
    if (assets.length === 0) {
      // Include model even with 0 assets
      rows.push({
        'Asset Code': '',
        'Alt Name': '',
        'Owner Code': '',
        'Location': '',
        'Sub Location': '',
        'Category': entry.category,
        'Type': entry.type,
        'SubType': entry.subtype,
        'Make': entry.make,
        'Model': entry.modelName,
      });
    } else {
      for (const a of assets) {
        rows.push({
          'Asset Code': a.assetCode,
          'Alt Name': a.altName,
          'Owner Code': a.ownerCode,
          'Location': a.location,
          'Sub Location': a.subLocation,
          'Category': entry.category,
          'Type': entry.type,
          'SubType': entry.subtype,
          'Make': entry.make,
          'Model': entry.modelName,
        });
      }
    }
  }

  if (rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const csvLines = [headers.join(',')];
  for (const row of rows) {
    csvLines.push(headers.map(h => {
      const val = (row[h] || '').toString().replace(/"/g, '""');
      return val.includes(',') || val.includes('"') || val.includes('\n') ? '"' + val + '"' : val;
    }).join(','));
  }

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Download button inline in node label
function DownloadBtn({ onClick }) {
  return (
    <span
      onClick={function(e) { e.stopPropagation(); onClick(); }}
      title="Download assets CSV"
      style={{
        cursor: 'pointer',
        marginLeft: 4,
        fontSize: 11,
        opacity: 0.6,
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
      onMouseEnter={function(e) { e.currentTarget.style.opacity = '1'; }}
      onMouseLeave={function(e) { e.currentTarget.style.opacity = '0.6'; }}
    >
      {'\u2B07'}
    </span>
  );
}

// Inner component
function FlowInner({ elements, onElementClick, minimapNodeColor }) {
  return (
    <ReactFlow
      elements={elements}
      onElementClick={onElementClick}
      minZoom={0.1}
      maxZoom={2}
      defaultZoom={0.8}
      fitView
      fitViewOnInit
      nodesDraggable={true}
      nodesConnectable={false}
      elementsSelectable={true}
      snapToGrid={true}
      snapGrid={[15, 15]}
    >
      <Controls />
      <MiniMap nodeColor={minimapNodeColor} style={{ bottom: 10, right: 10 }} />
      <Background color="#f0f0f0" gap={20} />
    </ReactFlow>
  );
}

function FlowWithZoomControl({ elements, clickedNodeRef, expandVersion, onElementClick, minimapNodeColor }) {
  const { setCenter, initialized } = useZoomPanHelper();
  const currentZoom = useStoreState(function(s) { return s.transform[2]; });
  const hasHandledVersion = useRef(0);
  const savedZoomRef = useRef(0.8);

  useEffect(() => {
    if (currentZoom) savedZoomRef.current = currentZoom;
  }, [currentZoom]);

  useEffect(() => {
    if (!initialized) return;
    if (expandVersion <= 0) return;
    if (hasHandledVersion.current >= expandVersion) return;
    hasHandledVersion.current = expandVersion;

    var clickedId = clickedNodeRef.current;
    if (!clickedId) return;

    var node = elements.find(function(el) {
      return isNode(el) && el.id === clickedId;
    });
    if (!node) return;

    var zoom = savedZoomRef.current || 0.8;
    var centerX = node.position.x + NODE_WIDTH / 2;
    var centerY = node.position.y + NODE_HEIGHT;
    setCenter(centerX, centerY, zoom);
  }, [elements, expandVersion, initialized, setCenter, clickedNodeRef]);

  return (
    <FlowInner
      elements={elements}
      onElementClick={onElementClick}
      minimapNodeColor={minimapNodeColor}
    />
  );
}

function ClassificationMindMap({ models, assetsByModelId }) {
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
  const [expandVersion, setExpandVersion] = useState(0);
  const clickedNodeRef = useRef(null);
  const assetsRef = useRef(assetsByModelId || {});
  assetsRef.current = assetsByModelId || {};

  // Build tree data — store model entries for download
  const treeData = useMemo(() => {
    const tree = {};
    for (const m of models) {
      if (m.status !== 'done') continue;
      const cls = m.new_category || 'Unclassified';
      const typ = m.new_type || 'Other';
      const sub = m.new_subtype || 'General';
      const assetModel = m.new_asset_model || m.current_model_name || '-';
      const assetCount = m.asset_count || 0;
      if (!tree[cls]) tree[cls] = {};
      if (!tree[cls][typ]) tree[cls][typ] = {};
      if (!tree[cls][typ][sub]) tree[cls][typ][sub] = {};
      if (!tree[cls][typ][sub][assetModel]) {
        tree[cls][typ][sub][assetModel] = { models: 0, assets: 0, modelEntries: [] };
      }
      tree[cls][typ][sub][assetModel].models++;
      tree[cls][typ][sub][assetModel].assets += assetCount;
      tree[cls][typ][sub][assetModel].modelEntries.push({
        model_id: m.model_id,
        modelName: assetModel,
        category: cls,
        type: typ,
        subtype: sub,
        make: m.new_make || m.current_make || '',
      });
    }
    return tree;
  }, [models]);

  // Keep a ref to treeData for download handlers inside node labels
  const treeRef = useRef(treeData);
  treeRef.current = treeData;

  // Download handler factory — returns a function that collects models from the right tree branch
  const makeDownloader = useCallback(function(level, clsName, typName, subName, modelName) {
    return function() {
      var tree = treeRef.current;
      var entries = [];
      var filename = 'assets';

      if (level === 'root') {
        entries = collectModelsFromTree(tree);
        filename = 'all-assets';
      } else if (level === 'cls') {
        entries = collectModelsFromTree(tree[clsName] || {});
        filename = clsName;
      } else if (level === 'typ') {
        entries = collectModelsFromTree((tree[clsName] || {})[typName] || {});
        filename = typName;
      } else if (level === 'sub') {
        entries = collectModelsFromTree(((tree[clsName] || {})[typName] || {})[subName] || {});
        filename = subName;
      } else if (level === 'mdl') {
        var leaf = (((tree[clsName] || {})[typName] || {})[subName] || {})[modelName];
        if (leaf && leaf.modelEntries) entries = leaf.modelEntries;
        filename = modelName;
      }

      if (entries.length === 0) return;
      var safeName = filename.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
      downloadAssetsCSV(entries, assetsRef.current, safeName + '-assets.csv');
    };
  }, []);

  const elements = useMemo(() => {
    const els = [];
    let colorIdx = 0;

    // Root node
    const totalCounts = sumTree(treeData);
    els.push({
      id: 'root',
      data: {
        label: (
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 14 }}>
            <div style={{ color: '#fff' }}>
              Category Map
              <DownloadBtn onClick={makeDownloader('root')} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>
              {totalCounts.m} models &middot; {totalCounts.a} assets
            </div>
          </div>
        ),
      },
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        border: '2px solid #5a3d8a',
        borderRadius: 12,
        width: 200,
        padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
      },
      position: { x: 0, y: 0 },
    });

    const classificationEntries = Object.entries(treeData).sort((a, b) => {
      const cA = sumTree(a[1]);
      const cB = sumTree(b[1]);
      return cB.a - cA.a || cB.m - cA.m;
    });

    classificationEntries.forEach(([clsName, typesObj]) => {
      const clsColor = CLASSIFICATION_COLORS[colorIdx % CLASSIFICATION_COLORS.length];
      colorIdx++;

      const clsCounts = sumTree(typesObj);
      const clsId = 'cls-' + clsName;
      const isClsExpanded = expandedNodes.has(clsId);

      els.push({
        id: clsId,
        data: {
          label: (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: clsColor.text }}>
                {clsName}
                <DownloadBtn onClick={makeDownloader('cls', clsName)} />
              </div>
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                {clsCounts.m} models &middot; {clsCounts.a} assets {isClsExpanded ? '\u25BC' : '\u25B6'}
              </div>
            </div>
          ),
        },
        style: {
          background: clsColor.bg,
          border: '2px solid ' + clsColor.border,
          borderRadius: 10,
          width: NODE_WIDTH,
          padding: '6px 10px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px ' + clsColor.border + '40',
        },
        position: { x: 0, y: 0 },
      });

      els.push({
        id: 'root->' + clsId,
        source: 'root',
        target: clsId,
        animated: false,
        style: { stroke: clsColor.border, strokeWidth: 2 },
      });

      if (isClsExpanded) {
        var typeColorIdx = 0;
        var typeEntries = Object.entries(typesObj).sort(function(a, b) {
          var cA = sumTree(a[1]);
          var cB = sumTree(b[1]);
          return cB.a - cA.a || cB.m - cA.m;
        });

        typeEntries.forEach(function(typeEntry) {
          var typName = typeEntry[0];
          var subsObj = typeEntry[1];
          var typColor = TYPE_COLORS[typeColorIdx % TYPE_COLORS.length];
          typeColorIdx++;
          var typCounts = sumTree(subsObj);
          var typId = 'typ-' + clsName + '-' + typName;
          var isTypExpanded = expandedNodes.has(typId);

          els.push({
            id: typId,
            data: {
              label: (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 600, fontSize: 11, color: '#333' }}>
                    {typName}
                    <DownloadBtn onClick={makeDownloader('typ', clsName, typName)} />
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>
                    {typCounts.m} models &middot; {typCounts.a} assets {isTypExpanded ? '\u25BC' : '\u25B6'}
                  </div>
                </div>
              ),
            },
            style: {
              background: typColor.bg,
              border: '2px solid ' + typColor.border,
              borderRadius: 8,
              width: NODE_WIDTH,
              padding: '6px 10px',
              cursor: 'pointer',
            },
            position: { x: 0, y: 0 },
          });

          els.push({
            id: clsId + '->' + typId,
            source: clsId,
            target: typId,
            style: { stroke: typColor.border, strokeWidth: 1.5 },
          });

          if (isTypExpanded) {
            var subEntries = Object.entries(subsObj).sort(function(a, b) {
              var cA = sumTree(a[1]);
              var cB = sumTree(b[1]);
              return cB.a - cA.a || cB.m - cA.m;
            });

            subEntries.forEach(function(subEntry) {
              var subName = subEntry[0];
              var modelsMap = subEntry[1];
              var subCounts = sumTree(modelsMap);
              var subId = 'sub-' + clsName + '-' + typName + '-' + subName;
              var isSubExpanded = expandedNodes.has(subId);
              var uniqueModels = Object.keys(modelsMap).length;

              els.push({
                id: subId,
                data: {
                  label: (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 500, fontSize: 11, color: '#27ae60' }}>
                        {subName}
                        <DownloadBtn onClick={makeDownloader('sub', clsName, typName, subName)} />
                      </div>
                      <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>
                        {uniqueModels} model{uniqueModels > 1 ? 's' : ''} &middot; {subCounts.a} assets
                        {uniqueModels > 0 ? (isSubExpanded ? ' \u25BC' : ' \u25B6') : ''}
                      </div>
                    </div>
                  ),
                },
                style: {
                  background: '#f0fdf4',
                  border: '1.5px solid #86efac',
                  borderRadius: 8,
                  width: NODE_WIDTH,
                  padding: '6px 10px',
                  cursor: 'pointer',
                },
                position: { x: 0, y: 0 },
              });

              els.push({
                id: typId + '->' + subId,
                source: typId,
                target: subId,
                style: { stroke: '#86efac', strokeWidth: 1.5 },
              });

              if (isSubExpanded) {
                Object.entries(modelsMap)
                  .sort(function(a, b) { return b[1].assets - a[1].assets || b[1].models - a[1].models; })
                  .forEach(function(modelEntry) {
                    var modelName = modelEntry[0];
                    var mData = modelEntry[1];
                    var modelId = 'mdl-' + clsName + '-' + typName + '-' + subName + '-' + modelName;

                    els.push({
                      id: modelId,
                      data: {
                        label: (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 10, fontWeight: 500, color: '#555' }}>
                              {modelName}
                              <DownloadBtn onClick={makeDownloader('mdl', clsName, typName, subName, modelName)} />
                            </div>
                            <div style={{ fontSize: 9, color: '#f39c12', marginTop: 1, fontWeight: 600 }}>
                              {mData.assets} asset{mData.assets !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ),
                      },
                      style: {
                        background: '#fff',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        width: NODE_WIDTH + 20,
                        padding: '4px 8px',
                        fontSize: 10,
                      },
                      position: { x: 0, y: 0 },
                    });

                    els.push({
                      id: subId + '->' + modelId,
                      source: subId,
                      target: modelId,
                      style: { stroke: '#d1d5db', strokeWidth: 1 },
                    });
                  });
              }
            });
          }
        });
      }
    });

    return getLayoutedElements(els, 'TB');
  }, [treeData, expandedNodes, models, makeDownloader]);

  var onElementClick = useCallback(function(event, element) {
    if (!isNode(element)) return;
    if (element.id.startsWith('mdl-')) return;
    if (element.id === 'root') return;

    clickedNodeRef.current = element.id;

    setExpandedNodes(function(prev) {
      var next = new Set(prev);
      if (next.has(element.id)) {
        var toRemove = [];
        next.forEach(function(id) {
          if (id === element.id || id.indexOf(element.id + '-') === 0) {
            toRemove.push(id);
          }
        });
        toRemove.forEach(function(id) { next.delete(id); });
      } else {
        next.add(element.id);
      }
      return next;
    });

    setExpandVersion(function(v) { return v + 1; });
  }, []);

  var minimapNodeColor = useCallback(function(node) {
    if (node.id === 'root') return '#764ba2';
    if (node.id.indexOf('cls-') === 0) return '#e94560';
    if (node.id.indexOf('typ-') === 0) return '#3498db';
    if (node.id.indexOf('sub-') === 0) return '#2ecc71';
    return '#95a5a6';
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <FlowWithZoomControl
          elements={elements}
          clickedNodeRef={clickedNodeRef}
          expandVersion={expandVersion}
          onElementClick={onElementClick}
          minimapNodeColor={minimapNodeColor}
        />
      </ReactFlowProvider>
    </div>
  );
}

export default ClassificationMindMap;
